import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { GLOBAL } from 'src/app/_config/config';
import { MarkAllAsReadRes, MarkAsReadRes, Notification, NotificationParams, NotificationResponse } from 'src/app/interfaces/notification.interfaces';
import { AuthService } from '../auth/auth.service';

/**
 * Servicio que gestiona las notificaciones del usuario, permitiendo recibir las
 * no leídas en tiempo real mediante SSE, listarlas con filtros y paginación, y
 * marcarlas como leídas de forma individual o masiva.
 */
@Injectable({
  providedIn: 'root'
})
export class NotificationsService {
  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}

  /**
   * Obtiene las últimas notificaciones no leídas en tiempo real mediante SSE
   * con autenticación, ignorando los eventos de heartbeat y conexión.
   * @returns Observable que emite cada notificación nueva recibida.
   */
  getLatestUnreadNotifications(): Observable<Notification> {
    return new Observable<Notification>(observer => {
      const url: string = `${GLOBAL.url}${GLOBAL.notification.unreadStream}`;

      // Obtener el token de autenticación usando el servicio de auth
      const token = this.authService.getToken();

      // Usar fetch con ReadableStream para SSE con headers
      fetch(url, {
        headers: {
          'Authorization': token,
          'Accept': 'text/event-stream'
        }
      }).then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const reader = response.body?.getReader();
        const decoder = new TextDecoder();

        const readStream = () => {
          reader?.read().then(({ done, value }) => {
            if (done) {
              observer.complete();
              return;
            }

            const chunk = decoder.decode(value);
            const lines = chunk.split('\n');

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                try {
                  const data = JSON.parse(line.substring(6));
                  // Solo emitir notificaciones reales, ignorar heartbeat y connected
                  if (data.type !== 'heartbeat' && data.type !== 'connected' && data.id) {
                    observer.next(data as Notification);
                  }
                } catch (error) {
                  console.error('Error parsing SSE notification data:', error);
                }
              }
            }

            readStream();
          }).catch(error => {
            observer.error(error);
          });
        };

        readStream();
      }).catch(error => {
        observer.error(error);
      });

      return () => {
        // Cleanup si es necesario
      };
    });
  }

  /**
   * Obtiene el listado de notificaciones aplicando filtros y paginación.
   * @param params - Parámetros opcionales de filtrado, paginación y ordenamiento.
   * @returns Observable con la respuesta paginada de notificaciones.
   */
  getNotifications(params: NotificationParams = {}): Observable<NotificationResponse> {
    const url: string = `${GLOBAL.url}${GLOBAL.notification.list}`;
    let httpParams = new HttpParams();

    if (params.page) httpParams = httpParams.set('page', params.page.toString());
    if (params.limit) httpParams = httpParams.set('limit', params.limit.toString());
    if (params.search) httpParams = httpParams.set('search', params.search);
    if (params.sortBy) httpParams = httpParams.set('sortBy', params.sortBy);
    if (params.sortOrder) httpParams = httpParams.set('sortOrder', params.sortOrder);
    if (params.readStatus) httpParams = httpParams.set('readStatus', params.readStatus);

    return this.http.get<NotificationResponse>(url, { params: httpParams });
  }

  /**
   * Marca una notificación como leída o no leída.
   * @param notification_id - Identificador de la notificación.
   * @param is_read - `true` para marcar como leída, `false` para no leída.
   * @returns Observable con el resultado de la operación.
   */
  markAsRead(notification_id: number, is_read: boolean): Observable<MarkAsReadRes> {
    const url: string = `${GLOBAL.url}${GLOBAL.notification.markAsRead}`;
    const requestBody = { notification_id, is_read };
    return this.http.post<MarkAsReadRes>(url, requestBody);
  }

  /**
   * Marca todas las notificaciones del usuario como leídas.
   * @returns Observable con el resultado de la operación.
   */
  markAllAsRead(): Observable<MarkAllAsReadRes> {
    const url: string = `${GLOBAL.url}${GLOBAL.notification.markAllAsRead}`;
    return this.http.post<MarkAllAsReadRes>(url, {});
  }
}
